/*
 * business-card/interact.js — the card as the interface.
 *
 * WHY THIS EXISTS
 * ───────────────
 * The first version of this tool was a FORM that produced a card: tabs at the
 * top, fields below, the card watching from the side. Measured on the shipped
 * page, that shape cost:
 *
 *   - 5 of 7 control panels hidden behind tabs at any moment, so the tool's
 *     capabilities were undiscoverable;
 *   - the export button 3.4 screens down on a laptop and 7.0 on a phone;
 *   - a click on the card doing nothing at all.
 *
 * The last one is the root. Everything a person wants to do to a card is
 * spatial — "this name is too big", "move the QR", "the logo should be over
 * there" — and a form can only express those as numbers in a box somewhere
 * else. So the card becomes the input device: click to select, drag to move,
 * grab a corner to resize, double-click to retype.
 *
 * The sibling /label-maker already had this and it was not reused — a mistake
 * worth naming, because the two hard-won lessons in its drag code are repeated
 * here rather than rediscovered:
 *
 *   1. BIND TO THE HOST, NOT THE CANVAS. Every model change redraws, and on a
 *      canvas tool the natural target survives — but the OVERLAY nodes do not.
 *      Listeners live on the wrapper, which outlives every repaint.
 *   2. CAPTURE THE POINTER ONLY ONCE THE GESTURE IS A DRAG. Capturing on
 *      pointerdown swallows the click that selects
 *      (reference_pointer_capture_on_down_kills_click). A threshold in
 *      millimetres, not pixels, keeps that consistent across zoom levels.
 *
 * WHY A DOM OVERLAY AND NOT DRAWING THE CHROME ON THE CANVAS
 * ─────────────────────────────────────────────────────────
 * The canvas is the PRINT SIMULATION — it must contain exactly what goes on
 * paper and nothing else. A selection outline drawn into it is one bug away
 * from being exported. Handles, badges and the inline editor are therefore
 * ordinary DOM positioned over the canvas: impossible to leak into the PDF,
 * crisp at every zoom, focusable, and reachable by keyboard.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CardInteract = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var MOVE_THRESHOLD_MM = 0.5;   // below this a pointerdown is still a click
    var NUDGE_MM = 0.5;            // arrow key step
    var NUDGE_FINE_MM = 0.1;       // with Shift

    function el(tag, cls, attrs) {
        var n = document.createElement(tag);
        if (cls) n.className = cls;
        if (attrs) for (var k in attrs) n.setAttribute(k, attrs[k]);
        return n;
    }

    /* ── hit testing ─────────────────────────────────────────────────────────
     * Topmost pickable item whose box contains the point. Painter's order means
     * the LAST match wins: a name drawn over a colour panel should select the
     * name. Only items the preset marked `pick` are candidates — decorative
     * bands belong to the layout, and letting people drag them apart would turn
     * a designed preset into a mess with no way back.
     *
     * The box is padded outward slightly so a 2.5 mm line of type is grabbable
     * without precision aiming; without it, selecting a phone number on a phone
     * screen is a test of fine motor control.
     */
    function hitTest(items, mmX, mmY, bounds, padMm) {
        padMm = padMm == null ? 0.8 : padMm;
        var found = null;
        for (var i = 0; i < items.length; i++) {
            var it = items[i];
            if (!it || !it.pick) continue;
            var b = bounds(it);
            if (mmX >= b.x - padMm && mmX <= b.x + b.w + padMm &&
                mmY >= b.y - padMm && mmY <= b.y + b.h + padMm) found = it;
        }
        return found;
    }

    /* ── the controller ──────────────────────────────────────────────────── */
    function mount(host, cfg) {
        if (!host) return null;
        if (host.__bcInteract) return host.__bcInteract;

        var overlay = el('div', 'bc-overlay');
        overlay.setAttribute('aria-hidden', 'false');
        host.appendChild(overlay);
        /* ⚠️ THE EDITOR GETS ITS OWN LAYER, outside the overlay.
         * `refresh()` rebuilds the overlay with innerHTML on every redraw, and
         * an open editor living inside it is destroyed the moment anything
         * changes — including the redraw its own first keystroke causes.
         * Measured on WebKit/iPhone: the editor could not be opened AT ALL,
         * by gesture or by button, while desktop Chromium worked, because
         * Safari blurs the fresh textarea, the blur commits, the commit
         * redraws, and the redraw removes the node. Desktop never blurred, so
         * the bug was invisible there. */
        var editLayer = el('div', 'bc-editlayer');
        editLayer.style.cssText = 'position:absolute;inset:0;pointer-events:none';
        host.appendChild(editLayer);
        host.style.touchAction = 'none';

        var state = { selected: null, editing: false, side: cfg.side };
        var nodes = { box: null, editor: null, flags: [] };

        /* mm → CSS px, using the same transform the canvas was drawn with. One
         * source, so a handle cannot drift from the ink it is framing. */
        function view() { return cfg.getView(); }
        function toPx(mm) { return mm * view().scale; }
        function xPx(mm) { var v = view(); return (v.padX + mm) * v.scale; }
        function yPx(mm) { var v = view(); return (v.padY + mm) * v.scale; }
        function fromPx(px, py) {
            var v = view();
            var r = host.getBoundingClientRect();
            return {
                x: (px - r.left) / v.scale - v.padX,
                y: (py - r.top) / v.scale - v.padY,
            };
        }

        function itemById(id) {
            var items = cfg.getItems();
            for (var i = 0; i < items.length; i++) if (items[i] && items[i].id === id) return items[i];
            return null;
        }

        /* ── rendering the chrome ───────────────────────────────────────── */
        function refresh() {
            overlay.innerHTML = '';
            /* The editor is not in the overlay and must survive a redraw — see
             * the note where `editLayer` is created. */
            var keep = nodes.editor;
            nodes = { box: null, editor: keep, flags: [] };
            drawSelection();
            drawFlags();
        }

        function drawSelection() {
            if (!state.selected) return;
            var it = itemById(state.selected);
            if (!it) { state.selected = null; return; }
            var b = cfg.bounds(it);

            var box = el('div', 'bc-sel');
            box.style.left = xPx(b.x) + 'px';
            box.style.top = yPx(b.y) + 'px';
            box.style.width = Math.max(6, toPx(b.w)) + 'px';
            box.style.height = Math.max(6, toPx(b.h)) + 'px';

            /* Only things with an intrinsic size get a resize grip. Text is
             * resized by its type scale, not by stretching a box — dragging a
             * corner of a text block in a layout that reflows would fight the
             * fitter, and the fitter would win. */
            /* The photograph gets one too: its "size" is the zoom of the crop,
             * which is what the hint under the card has promised all along
             * («угол меняет масштаб кадра») — and until 2026-08-26 the grip
             * was never drawn for it, and the drag path below started it
             * from a base of 0. */
            if (it.type === 'image' || it.type === 'qr' || it.type === 'photo') {
                var grip = el('b', 'bc-grip', { 'data-grip': '1', 'title': cfg.t('grip_hint') });
                box.appendChild(grip);
            }
            overlay.appendChild(box);
            nodes.box = box;
        }

        /* ── preflight markers, ON the card ──────────────────────────────────
         * The industry pattern for print editors (Customer's Canvas and the
         * shop-side tools built on it) is that a warning appears where the
         * problem is, not only in a list. A list at the bottom of a long page
         * asks the reader to map "текст близко к краю" onto one of eleven
         * blocks by themselves; a numbered marker on the offending block does
         * not.
         */
        /* ⚠️ ONE MARKER PER ELEMENT, NOT PER ISSUE. A 70×28 mm card with a long
         * job title produces nine issues across four blocks; drawn one badge
         * each they pile up on the same few corners, cover the design they are
         * describing, and — measured — physically intercept each other's
         * clicks, so the top one is the only one reachable. Grouping by element
         * also matches how the problem is actually fixed: you do not repair
         * "issue 6", you deal with the name.
         *
         * The badge therefore carries the COUNT, coloured by the worst severity
         * present, and the tie to the list below is the SELECTION rather than a
         * number — numbering both would need the two orders to agree forever. */
        function drawFlags() {
            var issues = cfg.getIssues() || [];
            var rank = { tip: 0, warn: 1, error: 2 };
            var byEl = {};
            issues.forEach(function (iss) {
                if (iss.side && iss.side !== state.side) return;
                if (!iss.id || !itemById(iss.id)) return;
                var g = byEl[iss.id] || (byEl[iss.id] = { id: iss.id, list: [], worst: 'tip' });
                g.list.push(iss);
                if (rank[iss.severity] > rank[g.worst]) g.worst = iss.severity;
            });
            Object.keys(byEl).forEach(function (id) {
                var g = byEl[id];
                var b = cfg.bounds(itemById(id));
                var f = el('button', 'bc-flag ' + g.worst, {
                    type: 'button',
                    'data-el': id,
                    title: g.list.map(function (i) { return i.__title; }).filter(Boolean).join(' · '),
                });
                f.textContent = g.list.length > 1 ? String(g.list.length) : '!';
                f.style.left = xPx(b.x + b.w) + 'px';
                f.style.top = yPx(b.y) + 'px';
                f.addEventListener('click', function (ev) {
                    ev.stopPropagation();
                    select(id);
                    cfg.onIssueClick && cfg.onIssueClick(g.list[0]);
                });
                overlay.appendChild(f);
                nodes.flags.push(f);
            });
        }

        /* ── selection ───────────────────────────────────────────────────── */
        function select(id) {
            if (state.selected === id) { refresh(); return; }
            state.selected = id;
            closeEditor();
            cfg.onSelect && cfg.onSelect(id, id ? itemById(id) : null);
            refresh();
        }

        /* ── inline text editing ─────────────────────────────────────────────
         * A textarea positioned over the block, styled to roughly match, so the
         * eye stays where the change is. It deliberately does NOT try to be a
         * pixel-perfect impostor: matching the canvas glyph-for-glyph is
         * impossible (the canvas is a print simulation, the textarea is the
         * browser's own text engine), and a near-miss reads as a rendering bug.
         * It reads as an editor instead — a clearly different surface, in the
         * same place, that commits on blur.
         */
        function openEditor(it) {
            /* ⚠️ `it.field` USED TO BE REQUIRED HERE, and a preset-composed
             * block (the monogram) has none — so double-clicking the initials
             * returned silently and the user concluded the card was broken.
             * Anything the layout marked editable opens; where the text goes
             * when it is committed is the host's problem, not this file's. */
            if (!it || it.type !== 'text' || !(it.field || it.editable)) return;
            closeEditor();
            var b = cfg.bounds(it);
            var ta = el('textarea', 'bc-inline');
            ta.value = (it.field ? cfg.getFieldValue(it.field) : it.raw) || '';

            /* ── SIZE IT FROM THE TYPE, NOT FROM THE BLOCK ───────────────────
             * ⚠️ This used to be `height = max(22, blockHeight + 10)`. A name
             * set at 4 mm previews at about 18 px, so the box came out 28 px
             * tall — and the font in it is floored at 16 px for the mobile-zoom
             * reason below. 16 px of type, two borders and two paddings do not
             * fit in 28 px: the value was clipped, a scrollbar appeared inside
             * a two-line box, and the thing read as broken. Reported as
             * «редактировать не удобно», with a screenshot of a text field the
             * size of a spinner.
             *
             * The box is a box for TEXT. Its height comes from the font it is
             * showing and the number of lines it holds; the block's own
             * millimetres only decide where it sits. */
            var fs = Math.min(30, Math.max(16, toPx(it.size) * 0.95));
            var lines = Math.max(1, (it.lines && it.lines.length) || 1);
            var padV = 10, padH = 12;

            var hostW = host.clientWidth || 320;
            var left = xPx(b.x);
            /* Wide enough to read, never wider than the card, never hanging off
             * its right edge. A right-aligned block near the trim would
             * otherwise open an editor half of which is outside the preview. */
            var wantW = Math.max(200, toPx(b.w) + padH * 2);
            var width = Math.min(wantW, hostW - 8);
            left = Math.max(4, Math.min(left, hostW - width - 4));

            ta.style.left = left + 'px';
            ta.style.top = Math.max(2, yPx(b.y) - padV) + 'px';
            ta.style.width = width + 'px';
            ta.style.fontSize = fs + 'px';
            ta.style.lineHeight = '1.3';
            ta.style.padding = (padV / 2) + 'px ' + (padH / 2) + 'px';
            ta.style.height = Math.round(fs * 1.3 * lines + padV + 4) + 'px';
            ta.setAttribute('rows', String(lines));
            ta.setAttribute('aria-label', cfg.t('edit_here'));
            ta.style.pointerEvents = 'auto';
            editLayer.appendChild(ta);

            /* Grow with the text rather than scroll it: an editor that hides
             * what you just typed is the same defect one size down. */
            var grow = function () {
                ta.style.height = 'auto';
                /* ⚠️ `scrollHeight` is content + padding and stops there, while
                 * `height` under this page's border-box sizing also has to pay
                 * for the border. Two pixels short is not "nearly right": it is
                 * a scrollbar inside a one-line editor, which is exactly the
                 * defect this rewrite is fixing. Measure the difference rather
                 * than assume it — the border is a stylesheet's business, not
                 * this file's. */
                var chrome = ta.offsetHeight - ta.clientHeight;
                ta.style.height = Math.min(240, ta.scrollHeight + chrome) + 'px';
            };
            ta.addEventListener('input', grow);
            grow();
            nodes.editor = ta;
            state.editing = true;

            /* Focus on the next frame. Taking focus synchronously inside the
             * gesture that opened the editor loses a race with Safari settling
             * focus on the element that was tapped, and the editor is blurred
             * before the user can type into it. */
            var armed = false;
            requestAnimationFrame(function () {
                try { ta.focus(); ta.select(); } catch (_) { }
                armed = true;
            });

            var commit = function () {
                /* A blur that arrives before the editor was ever focused is the
                 * browser settling, not the user finishing. Committing on it
                 * closes the editor the instant it opens. */
                if (!armed || !state.editing) return;
                state.editing = false;
                cfg.onEdit && cfg.onEdit(it.field, ta.value, it.id);
                closeEditor();
            };
            ta.addEventListener('blur', commit);
            ta.addEventListener('keydown', function (e) {
                e.stopPropagation();
                if (e.key === 'Escape') { state.editing = false; closeEditor(); refresh(); }
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ta.blur(); }
            });
        }
        function closeEditor() {
            /* The blur that `remove()` itself fires runs `commit` → closeEditor
             * again while the first removal is still on the stack; Chromium then
             * throws NotFoundError from the inner call (Sentry M5, the message
             * even says «moved in a 'blur' event handler»). Detach the reference
             * and the editing flag BEFORE touching the DOM, so the re-entrant
             * call finds nothing to do. */
            var ed = nodes.editor;
            nodes.editor = null;
            state.editing = false;
            if (ed) ed.remove();
        }

        /* ── the gesture ─────────────────────────────────────────────────────
         * Bound once, to the HOST. See the file header for why not the canvas
         * and why the capture is deferred.
         */
        host.addEventListener('pointerdown', function (ev) {
            if (ev.button != null && ev.button !== 0) return;
            if (state.editing) return;
            var onGrip = ev.target && ev.target.getAttribute
                && ev.target.getAttribute('data-grip') === '1';
            if (ev.target && ev.target.classList && ev.target.classList.contains('bc-flag')) return;

            var p = fromPx(ev.clientX, ev.clientY);
            var items = cfg.getItems();
            var target = onGrip ? itemById(state.selected)
                                : hitTest(items, p.x, p.y, cfg.bounds);

            if (!target) { select(null); return; }
            /* ⚠️ TOUCH HAS NO DOUBLE-CLICK. `dblclick` is a mouse concept; on
             * iOS a double-tap is the browser's zoom gesture and does not
             * reliably reach the page. Measured on two iPhone profiles: the
             * inline editor was simply unreachable — text on the card could be
             * moved but never retyped, on the platform most people will use.
             * The familiar substitute is the one every file manager uses:
             * tap to select, tap the SELECTED thing again to edit. */
            var wasSelected = (target.id === state.selected);
            if (!wasSelected) select(target.id);

            var start = { x: p.x, y: p.y };
            var b0 = cfg.bounds(target);
            var base = cfg.getOverride(target.id) || {};
            var baseDx = base.dx || 0, baseDy = base.dy || 0;
            var baseScale = (target.type === 'image' || target.type === 'photo') ? (base.scale || 1) : 0;
            var baseSize = (target.type === 'qr') ? cfg.getQrSize() : 0;
            var moved = false, captured = false;
            var isTouch = (ev.pointerType === 'touch');

            function onMove(e2) {
                var q = fromPx(e2.clientX, e2.clientY);
                var dx = q.x - start.x, dy = q.y - start.y;
                if (!moved && Math.abs(dx) < MOVE_THRESHOLD_MM && Math.abs(dy) < MOVE_THRESHOLD_MM) return;
                if (!moved) {
                    moved = true;
                    cfg.onGestureStart && cfg.onGestureStart();
                    try { host.setPointerCapture(e2.pointerId); captured = true; } catch (_) { }
                }
                if (onGrip) {
                    if (target.type === 'qr') {
                        /* The QR grows from its top-left, so the drag delta on
                         * the diagonal IS the size delta. Averaging the two
                         * axes keeps it square without fighting the pointer. */
                        cfg.onQrSize(baseSize + (dx + dy) / 2);
                    } else {
                        var grow = (b0.w + dx) / Math.max(1, b0.w);
                        cfg.onOverride(target.id, { scale: Math.max(0.25, Math.min(4, baseScale * grow)) });
                    }
                } else {
                    cfg.onOverride(target.id, { dx: baseDx + dx, dy: baseDy + dy });
                }
            }
            function onUp(e2) {
                host.removeEventListener('pointermove', onMove);
                host.removeEventListener('pointerup', onUp);
                host.removeEventListener('pointercancel', onUp);
                if (captured) { try { host.releasePointerCapture(e2.pointerId); } catch (_) { } }
                if (moved) { cfg.onGestureEnd && cfg.onGestureEnd(); return; }
                /* ⚠️ DECIDED ON RELEASE, NOT ON PRESS. Opening the editor from
                 * pointerdown looks equivalent and makes an already-selected
                 * element impossible to DRAG on touch — the press is consumed
                 * before anyone can find out whether it was going to move.
                 * Measured: after this shortcut, every touch drag of a selected
                 * text produced no movement at all. By pointerup the gesture has
                 * declared itself. */
                if (isTouch && wasSelected && target.type === 'text' && target.field) {
                    openEditor(target);
                }
            }
            host.addEventListener('pointermove', onMove);
            host.addEventListener('pointerup', onUp);
            host.addEventListener('pointercancel', onUp);
            /* No preventDefault: a plain tap must still reach the browser as a
             * click so focus and assistive technology behave normally. */
        });

        host.addEventListener('dblclick', function (ev) {
            /* A double-click on the open editor is word selection inside it,
             * not a request to reopen the block underneath (Sentry M5). */
            if (nodes.editor && ev.target && nodes.editor.contains(ev.target)) return;
            var p = fromPx(ev.clientX, ev.clientY);
            var target = hitTest(cfg.getItems(), p.x, p.y, cfg.bounds);
            if (target && target.type === 'text' && target.field) {
                select(target.id);
                openEditor(target);
            }
        });

        /* ── keyboard ────────────────────────────────────────────────────────
         * Arrows nudge by half a millimetre, Shift by a tenth. A card is a
         * physical object measured in millimetres, so the step is too — pixel
         * nudging would mean a different distance at every zoom level. */
        host.setAttribute('tabindex', '0');
        host.addEventListener('keydown', function (e) {
            if (state.editing) return;
            if (e.key === 'Escape') { select(null); return; }
            if (!state.selected) return;
            var it = itemById(state.selected);
            if (!it) return;

            if (e.key === 'Enter' || e.key === 'F2') {
                if (it.type === 'text' && it.field) { e.preventDefault(); openEditor(it); }
                return;
            }
            if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                cfg.onGestureStart && cfg.onGestureStart();
                cfg.onResetOverride(state.selected);
                cfg.onGestureEnd && cfg.onGestureEnd();
                return;
            }
            var step = e.shiftKey ? NUDGE_FINE_MM : NUDGE_MM;
            var dx = 0, dy = 0;
            if (e.key === 'ArrowLeft') dx = -step;
            else if (e.key === 'ArrowRight') dx = step;
            else if (e.key === 'ArrowUp') dy = -step;
            else if (e.key === 'ArrowDown') dy = step;
            else return;
            e.preventDefault();
            var o = cfg.getOverride(state.selected) || {};
            cfg.onGestureStart && cfg.onGestureStart();
            cfg.onOverride(state.selected, { dx: (o.dx || 0) + dx, dy: (o.dy || 0) + dy });
            cfg.onGestureEnd && cfg.onGestureEnd();
        });

        var ctl = {
            refresh: refresh,
            select: select,
            selected: function () { return state.selected; },
            setSide: function (s) { state.side = s; },
            editing: function () { return state.editing; },
            openEditorFor: function (id) { openEditor(itemById(id)); },
            canEdit: function (id) {
                var it = itemById(id);
                return !!(it && it.type === 'text' && it.field);
            },
        };
        host.__bcInteract = ctl;
        return ctl;
    }

    return { mount: mount, hitTest: hitTest, MOVE_THRESHOLD_MM: MOVE_THRESHOLD_MM };
});
